import { ChevronRight, Trophy } from "lucide-react";
import { Link } from "react-router";

import { MetricHead } from "@/components/metric-head";
import { RunOutcome } from "@/components/run-outcome";
import { ScoreCell } from "@/components/score-cell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Challenge, Submission } from "@/lib/api";
import {
  formatDegrees,
  formatError,
  formatRelative,
  formatSeconds,
  formatSteps,
  formatTimestamp,
} from "@/lib/format";
import { submissionHref } from "@/lib/links";
import { bestRun, formatSpl } from "@/lib/scoring";
import { cn } from "@/lib/utils";

/**
 * A student's attempts at one challenge, one row each, every row a link.
 *
 * The whole row opens the submission. The old design put the link on a "Job"
 * column showing the first eight characters of a UUID -- a target nobody was
 * looking for, labelled with a value nobody recognises. The id itself is on the
 * detail page for anyone who needs to quote it to a TA.
 *
 * Rows are numbered as attempts, oldest = 1, so the number matches the detail
 * page's "Attempt N" and stays put as new runs arrive. The list itself is newest
 * first, which is what a student checking on a fresh upload wants at the top.
 */
export function SubmissionsTable({
  challenge,
  submissions,
  className,
}: {
  challenge: Challenge;
  submissions: readonly Submission[];
  className?: string;
}) {
  const oldestFirst = [...submissions].sort((a, b) => a.submitted_at - b.submitted_at);
  const attemptNumber = new Map(oldestFirst.map((s, i) => [s.job_id, i + 1]));
  const spec = challenge.rubric_spec;
  const best = bestRun(submissions);
  const showHeading = spec.variables.includes("heading");
  const showSpl = spec.variables.includes("spl");
  const showTime = submissions.some((s) => s.wall_s !== null);

  return (
    <div className={cn("overflow-hidden rounded-lg border", className)}>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/40 hover:bg-muted/40">
            <TableHead className="w-12 text-right">#</TableHead>
            <TableHead>Submitted</TableHead>
            <MetricHead metric="result" />
            <MetricHead metric="score" className="text-right" />
            <MetricHead metric="steps" className="text-right" />
            {showSpl ? <MetricHead metric="spl" className="hidden text-right md:table-cell" /> : null}
            <MetricHead metric="distance" className="hidden text-right sm:table-cell" />
            {showHeading ? (
              <MetricHead metric="heading" className="hidden text-right md:table-cell" />
            ) : null}
            {showTime ? <MetricHead metric="time" className="hidden text-right md:table-cell" /> : null}
            <TableHead className="w-8" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...oldestFirst].reverse().map((row) => {
            const isBest = best?.job_id === row.job_id;
            return (
              // `relative` so the stretched link in the first cell covers the row.
              <TableRow
                key={row.job_id}
                className="group/row has-[a:focus-visible]:bg-muted/50 relative cursor-pointer"
              >
                <TableCell className="text-muted-foreground text-right tabular-nums">
                  <Link
                    to={submissionHref(challenge.id, row)}
                    className="after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
                    aria-label={`Attempt ${attemptNumber.get(row.job_id)}, ${formatTimestamp(row.submitted_at)}`}
                  >
                    {attemptNumber.get(row.job_id)}
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {/* Phones get the short form only; the exact time is on the detail page. */}
                  <span className="sm:hidden">{formatRelative(row.submitted_at)}</span>
                  <span className="hidden sm:inline">
                    {formatTimestamp(row.submitted_at)}
                  </span>
                  <span className="text-muted-foreground hidden text-xs lg:inline">
                    {" · "}
                    {formatRelative(row.submitted_at)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1.5">
                    <RunOutcome submission={row} />
                    {isBest && submissions.length > 1 ? (
                      <Trophy
                        className="text-primary size-3.5"
                        aria-label="Your best"
                      />
                    ) : null}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <ScoreCell points={row.score?.points ?? null} max={spec.max_points} />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatSteps(row.nav_steps)}
                </TableCell>
                {showSpl ? (
                  <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                    {formatSpl(row.spl)}
                  </TableCell>
                ) : null}
                <TableCell
                  className={cn(
                    "hidden text-right tabular-nums sm:table-cell",
                    row.arrival === null && "text-muted-foreground",
                  )}
                >
                  {formatError(row.arrival?.distance_m ?? null)}
                </TableCell>
                {showHeading ? (
                  <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                    {formatDegrees(row.arrival?.heading_deg ?? null)}
                  </TableCell>
                ) : null}
                {showTime ? (
                  <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                    {formatSeconds(row.wall_s)}
                  </TableCell>
                ) : null}
                <TableCell className="text-muted-foreground pr-3 text-right">
                  <ChevronRight
                    className="group-hover/row:text-foreground inline size-4 transition-colors"
                    aria-hidden
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
